'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { siteConfig } from '../../../config/site';
import { PhoneIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [headingRef?.current, subRef?.current, ctaRef?.current, statsRef?.current];
    els?.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      el.style.filter = 'blur(4px)';
      setTimeout(() => {
        if (!el) return;
        el.style.transition = 'opacity 0.8s cubic-bezier(0.4,0,0.2,1), transform 0.8s cubic-bezier(0.4,0,0.2,1), filter 0.8s cubic-bezier(0.4,0,0.2,1)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        el.style.filter = 'blur(0)';
      }, 200 + i * 180);
    });
  }, []);

  return (
    <section
      className="relative overflow-hidden min-h-[90vh] flex flex-col justify-center"
      style={{ background: 'var(--bg)' }}
      aria-label="Hero">
      
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(57,255,20,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
        aria-hidden="true" />
      
      {/* Radial glow */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(57,255,20,0.06) 0%, transparent 70%)', filter: 'blur(40px)', transform: 'translateY(-30%)' }}
        aria-hidden="true" />
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="space-y-6">
            {/* Eyebrow */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{ borderColor: 'rgba(57,255,20,0.3)', backgroundColor: 'rgba(57,255,20,0.06)', color: 'var(--brand)' }}>
              
              <span className="neon-dot" />
              Long Island City, NY · Serving All of New York State
            </div>

            <h1 ref={headingRef} className="text-hero-xl font-bold" style={{ color: 'var(--text)', opacity: 1 }}>
              Expert Device Repair{' '}
              <span className="brand-gradient-text">in Long Island City, NY.</span>
            </h1>

            <p ref={subRef} className="text-lg leading-relaxed max-w-xl" style={{ color: 'var(--text-muted)', opacity: 1 }}>
              Repair for iPhones, Samsung phones, laptops, MacBooks, PS5, Xbox, Nintendo Switch and more. Visit our shop or request on-site service anywhere in New York State.
            </p>

            {/* Badge row */}
            <div className="flex flex-wrap gap-2">
              {['Same-day repair available', 'Shop & On-Site Service', 'Serving All of New York State']?.map((badge) =>
              <span
                key={badge}
                className="px-3 py-1 rounded-full text-xs font-medium border"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)', backgroundColor: 'var(--surface)' }}>
                
                  {badge}
                </span>
              )}
            </div>

            {/* CTAs */}
            <div ref={ctaRef} className="flex flex-wrap gap-3" style={{ opacity: 1 }}>
              <Link
                href="/request-service"
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}>
                
                Request a Repair
              </Link>
              <a
                href={siteConfig?.phone?.tel}
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}>
                
                <PhoneIcon className="w-4 h-4" style={{ color: 'var(--brand)' }} />
                Call Now
              </a>
              <a
                href={siteConfig?.phone?.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}>
                
                WhatsApp Us
              </a>
            </div>
          </div>

          {/* Right: Info card + image */}
          <div className="space-y-4">
            {/* Hero image */}
            <div className="relative rounded-2xl overflow-hidden aspect-video" style={{ border: '1px solid var(--border-color)' }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_168cb44bf-1773521302750.png"
                alt="Electronics repair workbench with tools, circuit boards and professional lighting in a Long Island City repair shop"
                fill
                className="object-cover"
                priority
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(11,13,15,0.6) 0%, transparent 60%)' }} />
            </div>

            {/* Address / hours card */}
            <div
              ref={statsRef}
              className="rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4"
              style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)', opacity: 1 }}>
              
              <div className="flex items-start gap-2.5">
                <MapPinIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--brand)' }} />
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Address</div>
                  <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>38-33 29th St<br />Long Island City, NY 11101</div>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <PhoneIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--brand)' }} />
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Phone</div>
                  <a href={siteConfig?.phone?.tel} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: 'var(--text)' }}>
                    {siteConfig?.phone?.display}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <ClockIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--brand)' }} />
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Hours</div>
                  <div className="text-sm" style={{ color: 'var(--text)' }}>
                    <div>Mon–Sat: 11:30 AM–7:30 PM</div>
                    <div>Sun: 1:00–8:00 PM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Review strip */}
        <div className="mt-10 pt-8 border-t flex flex-wrap items-center gap-4" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-label="Google">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <div className="flex">
              {[1, 2, 3, 4, 5]?.map((s) =>
              <svg key={s} className="w-4 h-4" viewBox="0 0 20 20" fill="#FBBC05">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>500+ Google reviews</span>
          </div>
          <Link href="/reviews" className="text-sm font-medium transition-opacity hover:opacity-80" style={{ color: 'var(--brand)' }}>
            Read our reviews →
          </Link>
        </div>
      </div>
    </section>);

}